import { Colors, Heading, PageContainer, Text } from '@amelio/ui-library';
import { useParams } from 'react-router-dom';

import { toFormData, toUpdateRequest } from './meetingPlanTemplateDraft';
import { MeetingPlanTemplateForm } from './MeetingPlanTemplateForm';

import type { MeetingPlanTemplateFormData } from './validation';
import type { ReactNode } from 'react';

import { useGetEditableMeetingTemplates, useUpdateMeetingTemplate } from '@/api/thematic-meetings';
import { BackLink } from '@/components/BackLink';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import { showSuccessToast } from '@/plugins/Toast';
import { toContentLanguage } from '@/utils/contentLanguage';
import { getTextByLanguage } from '@/utils/localization';

/**
 * The frame every state of the editor shares, so none of them can end up without a way out again.
 *
 * The way back is a `button` driven by the module's `useNavigate`, not an anchor: the host owns the
 * router, and a local push would change the address while leaving the host's menu lit on the page we
 * came from. Every other back link of the module is written the same way.
 *
 * Capped narrower than `PageContainer`'s own width, and centered: a plan's fields (a title, one or
 * two lines of text) read worse spread over the page's full width than over a column sized to them.
 * `PageContainer` takes no width of its own, so the cap is written here, on this page alone.
 */
const EditorShell = ({ onBack, children }: { onBack: () => void; children: ReactNode }) => {
    const { t } = useAmelioTranslations();

    return (
        <PageContainer>
            <div className={'mx-auto flex max-w-[1024px] flex-col gap-6'}>
                <BackLink
                    onClick={onBack}
                    label={t('templateManagement.meetingPlanEditor.backToTemplates')}
                    dataTestId={'meeting-plan-template-back'}
                />

                {children}
            </div>
        </PageContainer>
    );
};

/**
 * Edits one of the plans the company's conversations are built from.
 *
 * The address names the plan by its id, never by its topic: a company may hold several plans for one
 * topic. The plans are stored bilingually and only the reader's own language is edited here, exactly
 * as a meeting's agenda is; the wording in the other language travels through the form untouched and
 * comes back unchanged.
 */
export const MeetingPlanTemplateEditorPage = () => {
    const { t, i18n } = useAmelioTranslations();
    const navigate = useNavigate();
    const { handleError } = useError();
    const { templateId } = useParams<{ templateId: string }>();

    // The language the dictionaries are keyed by ("fr", "en"), never the raw "fr_FR" the host may
    // report: writing under an unnormalized key would add a third wording nothing ever reads.
    const language = toContentLanguage(i18n.language);

    const {
        data: templates,
        isLoading,
        isError,
        isFetching,
        refetch,
    } = useGetEditableMeetingTemplates();
    const { mutate: updateTemplate, isPending: isSaving } = useUpdateMeetingTemplate();

    const template = templates?.find((candidate) => candidate.id === templateId);

    const goBackToTemplates = () => navigate({ path: RouteName.ManageTemplates });

    const handleSubmit = (data: MeetingPlanTemplateFormData) => {
        if (!template) {
            return;
        }

        updateTemplate(
            { id: template.id, request: toUpdateRequest(template, data, language) },
            {
                onSuccess: () => {
                    showSuccessToast({
                        message: t('templateManagement.meetingPlanEditor.saved'),
                    });
                    goBackToTemplates();
                },
                onError: (error) => handleError({ error }),
            },
        );
    };

    if (isLoading) {
        return (
            <EditorShell onBack={goBackToTemplates}>
                <Skeleton>
                    <div
                        className={'flex flex-col gap-6'}
                        data-testid={'meeting-plan-template-skeleton'}
                    >
                        <Bone className={'h-7.5 w-50'} />
                        <Bone className={'h-30 w-full'} />
                        <Bone className={'h-60 w-full'} />
                    </div>
                </Skeleton>
            </EditorShell>
        );
    }

    // The same failure, treated the way the creation flow's first step treats it: what happened, and
    // the one gesture that can undo it.
    if (isError) {
        return (
            <EditorShell onBack={goBackToTemplates}>
                <div data-testid={'meeting-plan-template-error'}>
                    <EmptyState
                        message={t('templateManagement.meetingPlanEditor.loadError')}
                        action={{
                            label: t('templateManagement.meetingPlanEditor.retry'),
                            onClick: () => void refetch(),
                            disabled: isFetching,
                        }}
                    />
                </div>
            </EditorShell>
        );
    }

    // Loaded, but this company holds no living plan of the id in the address — a plan it has
    // deleted, or an address typed by hand; a draft is living, and opens like any plan. Said plainly
    // rather than shown as an empty form that would save nothing. Nothing here can be retried, so the
    // shell's way back is the only exit offered.
    if (!template) {
        return (
            <EditorShell onBack={goBackToTemplates}>
                <div data-testid={'meeting-plan-template-not-found'}>
                    <EmptyState message={t('templateManagement.meetingPlanEditor.notFound')} />
                </div>
            </EditorShell>
        );
    }

    return (
        <EditorShell onBack={goBackToTemplates}>
            <div
                className={'flex flex-col gap-6'}
                data-testid={'meeting-plan-template-content'}
            >
                {/* The plan's own name is the page's title, the way a meeting's plan leads with
                    the meeting's: the nine plans shared one heading, so two tabs open on two of
                    them read alike, and the only thing naming the open one was the first field —
                    which is editable, and may have been emptied and retyped. What this page is
                    stays above the name, in the smaller line. */}
                <div className={'flex flex-col gap-1'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_500}
                    >
                        {t('templateManagement.meetingPlanEditor.heading')}
                    </Text>

                    <Heading level={4}>{getTextByLanguage(template.title, language)}</Heading>
                </div>

                <MeetingPlanTemplateForm
                    defaultValues={toFormData(template, language)}
                    language={language}
                    topic={template.topic}
                    isSaving={isSaving}
                    onSubmit={handleSubmit}
                    onCancel={goBackToTemplates}
                />
            </div>
        </EditorShell>
    );
};
